/**
 * Holds the active locale for the whole app and re-renders in place when it changes — no
 * remount, no reload, no navigation — so switching language mid-battle loses nothing (REQ-03).
 *
 * Inputs, in the precedence `resolveLocale` applies: an optional URL locale (public pages), the
 * device's stored explicit choice, an optional account preference, `navigator.languages`.
 * `languagechange` refreshes the browser list; because an explicit choice outranks it, the
 * browser only ever moves a player who has not chosen (REQ-05). A choice made in another tab
 * is picked up through the `storage` event.
 *
 * A non-English catalog is fetched on demand. Until it arrives the previous language stays on
 * screen; if it never arrives, English does, and the failure is reported as fallbacks.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  LOCALES,
  PSEUDO_LOCALE,
  getEnabledLocaleCodes,
  getLocaleDefinition,
  type AppLocale,
  type LocaleCode,
} from "@shared/i18n/localeConfig";
import { matchLocale } from "@shared/i18n/localeMatch";
import { resolveLocale, type StoredLocaleChoice } from "@shared/i18n/localePreference";
import { EN_CATALOG, type Catalog } from "./englishCatalog";
import { createFormatters } from "./format";
import { I18nContext, keyChunks, type I18nContextValue } from "./i18nContext";
import { reportFallback, reportLocaleChanged, reportLocaleResolved } from "./i18nAnalytics";
import { loadLocaleCatalog } from "./loadCatalog";
import {
  LOCALE_STORAGE_KEY,
  clearStoredLocaleChoice,
  readStoredLocaleChoice,
  writeStoredLocaleChoice,
} from "./localePreferenceStore";
import { I18N_DEV_TOOLS } from "./i18nEnv";
import { Translator } from "./translator";
import { setActiveTranslator } from "./translate";

/** The pseudolocale exists in development and tests only — never in a production bundle. */
const PSEUDO_ENABLED = I18N_DEV_TOOLS;

function readBrowserLocales(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages && navigator.languages.length > 0) return [...navigator.languages];
  return navigator.language ? [navigator.language] : [];
}

/** English is bundled; the pseudolocale is derived from it, in dev only, in its own chunk. */
async function catalogFor(locale: AppLocale, loadReal: (locale: LocaleCode) => Promise<Catalog>): Promise<Catalog> {
  if (locale === DEFAULT_LOCALE) return EN_CATALOG;
  if (locale === PSEUDO_LOCALE) {
    const { pseudoLocalizeCatalog } = await import("@shared/i18n/pseudoLocalize");
    return pseudoLocalizeCatalog(EN_CATALOG);
  }
  return loadReal(locale);
}

async function loadCatalogFor(locale: LocaleCode): Promise<Catalog> {
  const { catalog, failedNamespaces } = await loadLocaleCatalog(locale);
  if (failedNamespaces.length > 0 && I18N_DEV_TOOLS) {
    console.warn(`[i18n] ${locale}: could not load ${failedNamespaces.join(", ")}; those keys show English.`);
  }
  return catalog;
}

export interface I18nProviderProps {
  readonly children: ReactNode;
  /** An explicit locale from a public-page URL (`/es/...`). Applies to that page only. */
  readonly urlLocale?: string | null;
  /** The signed-in account's saved preference, if any. */
  readonly accountLocale?: string | null;
  /** Test seam: the locales that may be shown. Defaults to every enabled locale. */
  readonly availableLocales?: readonly LocaleCode[];
  /** Test seam: loader for a real (non-English) locale's catalog. */
  readonly loadCatalog?: (locale: LocaleCode) => Promise<Catalog>;
}

export function I18nProvider({
  children,
  urlLocale = null,
  accountLocale = null,
  availableLocales,
  loadCatalog = loadCatalogFor,
}: I18nProviderProps) {
  const available = useMemo(() => availableLocales ?? getEnabledLocaleCodes(), [availableLocales]);
  const [choice, setChoice] = useState<StoredLocaleChoice | null>(readStoredLocaleChoice);
  const [browserLocales, setBrowserLocales] = useState<readonly string[]>(readBrowserLocales);
  const [catalogs, setCatalogs] = useState<ReadonlyMap<AppLocale, Catalog>>(
    () => new Map<AppLocale, Catalog>([[DEFAULT_LOCALE, EN_CATALOG]]),
  );

  const resolved = useMemo(
    () =>
      resolveLocale(
        { urlLocale, manualLocale: choice?.locale, accountLocale, browserLocales },
        { available, allowPseudo: PSEUDO_ENABLED },
      ),
    [urlLocale, choice, accountLocale, browserLocales, available],
  );
  const explicitLocale = useMemo(() => {
    if (!choice) return null;
    const r = resolveLocale({ manualLocale: choice.locale }, { available, allowPseudo: PSEUDO_ENABLED });
    return r.source === "manual" ? r.locale : null;
  }, [choice, available]);
  const automaticLocale = useMemo(
    () => matchLocale(browserLocales, available) ?? DEFAULT_LOCALE,
    [browserLocales, available],
  );

  // The locale actually on screen: the resolved one once its catalog is in, else the last one shown.
  const [shownLocale, setShownLocale] = useState<AppLocale>(DEFAULT_LOCALE);
  const wanted = resolved.locale;
  const wantedReady = catalogs.has(wanted);
  useEffect(() => {
    if (wantedReady) {
      setShownLocale(wanted);
      return;
    }
    let cancelled = false;
    catalogFor(wanted, loadCatalog)
      .then((catalog) => {
        if (cancelled) return;
        setCatalogs((prev) => new Map(prev).set(wanted, catalog));
      })
      .catch(() => {
        // The whole locale is unreachable: English stays, every key reports a fallback on use.
        if (cancelled) return;
        setCatalogs((prev) => new Map(prev).set(wanted, {}));
      });
    return () => {
      cancelled = true;
    };
  }, [wanted, wantedReady, loadCatalog]);

  const locale: AppLocale = catalogs.has(shownLocale) ? shownLocale : DEFAULT_LOCALE;
  const source = locale === wanted ? resolved.source : "default";

  // REQ-05: follow the browser's own language setting while the player has not chosen.
  useEffect(() => {
    const onLanguageChange = () => setBrowserLocales(readBrowserLocales());
    const onStorage = (e: StorageEvent) => {
      if (e.key === LOCALE_STORAGE_KEY || e.key === null) setChoice(readStoredLocaleChoice());
    };
    window.addEventListener("languagechange", onLanguageChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("languagechange", onLanguageChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // The document says which language it is in (screen readers, hyphenation, CJK fonts, SEO).
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = getLocaleDefinition(locale)?.direction ?? "ltr";
  }, [locale]);

  const previous = useRef<AppLocale | null>(null);
  const pendingSelection = useRef<"manual" | "automatic">("automatic");
  useEffect(() => {
    if (previous.current === null) reportLocaleResolved(locale, source);
    else if (previous.current !== locale) reportLocaleChanged(previous.current, locale, pendingSelection.current);
    previous.current = locale;
    pendingSelection.current = "automatic";
  }, [locale, source]);

  const setLocale = useCallback((next: AppLocale) => {
    pendingSelection.current = "manual";
    setChoice(writeStoredLocaleChoice(next));
  }, []);
  const clearLocaleChoice = useCallback(() => {
    pendingSelection.current = "manual";
    clearStoredLocaleChoice();
    setChoice(null);
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const translator = new Translator({
      locale,
      messages: catalogs.get(locale) ?? EN_CATALOG,
      fallback: EN_CATALOG,
      onFallback: reportFallback,
      revealMissingKeys: I18N_DEV_TOOLS,
    });
    // Set while rendering (before the children render), so text built outside React during this
    // render already speaks the new language.
    setActiveTranslator(translator);
    return {
      locale,
      source,
      explicitLocale,
      automaticLocale,
      isLoading: !wantedReady,
      selectableLocales: LOCALES.filter((l) => available.includes(l.code)),
      pseudoAvailable: PSEUDO_ENABLED,
      t: (key, values) => translator.t(key, values),
      rich: (key, values) => keyChunks(translator.rich<ReactNode>(key, values)),
      format: createFormatters(locale),
      setLocale,
      clearLocaleChoice,
    };
  }, [locale, source, explicitLocale, automaticLocale, wantedReady, catalogs, available, setLocale, clearLocaleChoice]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
